import { useAppDispatch, useAppSelector } from "@/shared/hooks/store";
import MedicalCard from "../medical-card"
import { selectIsOnlyOneMedication, selectUserUserOnboarding } from "@/store/slices/onboarding/selectors";
import { IPrescribables } from "@/shared/helpers/main";
import { useCallback, useEffect, useMemo } from "react";
import _ from "lodash";
import { CustomersController, CustomersStepTypeEnum, CustomersStepTypeEnumPaths, ICustomersInsuranceManualResponse, ICustomersInsuranceTestClaimPrescription, IPrescriptionCopayQuantity } from "@/shared/endpoints/customers";
import { URLSearchParamsInit, useNavigate } from "react-router-dom";
import { ROUTES } from "@/shared/constants/routes";
import { IMedicationQuantityInitialValues } from "../../../../../../shared/constants/medication-quantity";
import { isCopayRequestLimitReached } from "@/shared/helpers/test-claim";

interface IProps {
    showCopay: boolean;
    claims: Record<string, ICustomersInsuranceTestClaimPrescription>;
    prescribables: IPrescribables;
    eligibleInsurances: ICustomersInsuranceManualResponse[];
    navigateToStep?: (step: CustomersStepTypeEnumPaths, index?: number, searchParams?: URLSearchParamsInit) => void;
    onQuantityChange?(medid: number, quantity: IPrescriptionCopayQuantity): void;
    copayRequestsExhausted: boolean;
};

const ReviewMedicationList = ({ showCopay, claims, prescribables, eligibleInsurances, navigateToStep, onQuantityChange, copayRequestsExhausted }: IProps) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const userOnboarding = useAppSelector(selectUserUserOnboarding);
    const isOnlyOneMedication = useAppSelector(selectIsOnlyOneMedication);
    const lastMedicationAlertString = `You are about to remove your ${isOnlyOneMedication ? '' : 'last'} medication. This will take you back to the “My MedBox” section.`;
    const medicationsLength = prescribables.includingMedbox.length + prescribables.packagedSeparately.length;

    useEffect(() => {
        // if the medications are reduced to 0, take them back to prescriptions step and mark the step so review is disabled 
        if (medicationsLength === 0 && userOnboarding) {
            const payload = _.cloneDeep(userOnboarding);
            payload.details = { ...payload.details!, currentStep: CustomersStepTypeEnum.Prescriptions };
            dispatch(CustomersController.updateMetadata(payload));
            // if (navigateToStep) navigateToStep(CustomersStepTypeEnumPaths.PRESCRIPTIONS);
            navigate(
                `/${ROUTES.ONBOARDING.MAIN}/${ROUTES.ONBOARDING.PRESCRIPTIONS.MAIN}/${ROUTES.ONBOARDING.PRESCRIPTIONS.MEDICATIONS}`
            );

        }
    }, [dispatch, medicationsLength, navigate, userOnboarding]);

    const handleOnQuantityChange = useCallback(async (medid: number, value: IMedicationQuantityInitialValues) => {
        const payload = _.cloneDeep(userOnboarding!);

        const editedQuantity = {
            quantity: +value.quantity,
            daysSupply: value.daysSupply,
        };

        payload.details = {
            ...payload.details!,
            prescriptionCopayQuantities: {
                ...(payload.details?.prescriptionCopayQuantities || {}),
                [medid]: editedQuantity,
            }
        };

        await dispatch(CustomersController.updateMetadata(payload));

        onQuantityChange?.(medid, editedQuantity);
    }, [userOnboarding]);

    const totalCopay = useMemo(() => {
        if (
            !Object.keys(claims).length ||
            !showCopay ||
            Object.keys(claims).every(key => claims[key].coPay == null)
        ) return null;

        return _.reduce(claims, (acc, presc) => {
            if (claims[presc.medid]?.coPay == null) {
                return acc;
            }
            return ((acc * 100) + (presc?.coPay * 100)) / 100
        }, 0);
    }, [showCopay, claims]);

    const getCopayAmount = useCallback((medid: number) => {
        const claim = claims[medid];
        if (!claim) return null;
        return claim.coPay;
    }, [showCopay, claims]);


    return (
        <>
            <div className="w-full border-b-2 border-mint-200 pb-7">
                {!!prescribables.includingMedbox.length && (
                    <div className="flex flex-col gap-3 pt-5 md:pt-9">
                        <h3 className="font-atkinsonBold text-lg">In Your MedBox</h3>
                        <div className="flex flex-col gap-4">
                            {prescribables.includingMedbox.map((e, eIndex) => {
                                return (
                                    <MedicalCard
                                        copayRequestsExhausted={copayRequestsExhausted}
                                        key={e.medid}
                                        medication={e}
                                        shouldShowEditQuantityTooltip={eIndex === 0}
                                        claim={claims[e.medid]}
                                        hasEditQuantity={eligibleInsurances.length === 1 && !isCopayRequestLimitReached(userOnboarding)}
                                        showCopay={showCopay}
                                        copayAmount={getCopayAmount(+e.medid)}
                                        onQuantityChange={value => handleOnQuantityChange(+e.medid, value)}
                                        deletionModalText={medicationsLength === 1 ? lastMedicationAlertString : ''}
                                    />
                                )
                            })}
                        </div>
                    </div>
                )}

                {!!prescribables.packagedSeparately.length && (
                    <div className="flex flex-col gap-3 pt-5 md:pt-7">
                        <h3 className="font-atkinsonBold text-lg">Packaged Separately</h3>
                        <div className="flex flex-col gap-4">
                            {prescribables.packagedSeparately.map((e, eIndex) => (
                                <MedicalCard
                                    copayRequestsExhausted={copayRequestsExhausted}
                                    key={e.medid}
                                    medication={e}
                                    shouldShowEditQuantityTooltip={!prescribables.includingMedbox.length && eIndex === 0}
                                    claim={claims[e.medid]}
                                    hasEditQuantity={eligibleInsurances.length === 1}
                                    showCopay={showCopay}
                                    copayAmount={getCopayAmount(+e.medid)}
                                    onQuantityChange={value => handleOnQuantityChange(+e.medid, value)}
                                    deletionModalText={medicationsLength === 1 ? lastMedicationAlertString : ''}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {
                totalCopay !== null && (
                    <div className="animate-appear flex justify-between border-b-2 border-mint-200 pb-[1.5625rem] mt-[1.5625rem]">
                        <h2 className="font-atkinsonBold text-xl">Estimated Copays</h2>
                        <h2 className="font-atkinsonBold text-xl pl-1">${totalCopay.toFixed(2)}</h2>
                    </div>
                )
            }
        </>
    )
}

export default ReviewMedicationList;